import React from "react";
import {
  Grid,
  Paper,
  Card,
  InputLabel,
  Typography,
  TextField,
  Autocomplete,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import useStyles from "./AllData.styles";
import ColumnGroupingTable from "../../table/ColumnGroupingTable.component";
import WhatWeDo from "../../whatWeDo/WhatWeDo.component";
import { useBloombergContext } from "../../../context/BloombergContext";
import { allOption } from "../../../utils/constants";
import AllDataExport from "./AllDataExport";
import { formatDate } from "../../../utils/utils";
import { CUSTOM } from "../../../utils/constants";
import DateRangeDatePickerWithPresets from "../../common/DatePicker/DateRangeDatePickerWithPresets";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const columns = [
  { id: "ticker", label: "Ticker", minWidth: 120 },
  { id: "field", label: "Field", minWidth: 120 },
  {
    id: "reportDate",
    label: "Report Date",
    minWidth: 50,
    format: (value) => value.toLocaleString("en-US"),
  },
  {
    id: "numericValue",
    label: "Numeric Value",
    minWidth: 50,
    type: "float",
  },
  {
    id: "stringValue",
    label: "String Value",
    minWidth: 100,
  },
  {
    id: "dateValue",
    label: "Date Value",
    minWidth: 100,
  },
  {
    id: "lastUpdated",
    label: "Last Updated",
    minWidth: 100,
  },
];

function createData(
  ticker,
  field,
  reportDate,
  numericValue,
  stringValue,
  dateValue,
  lastUpdated,
) {
  return {
    ticker,
    field,
    reportDate,
    numericValue,
    stringValue,
    dateValue: formatDate(dateValue),
    lastUpdated: formatDate(lastUpdated),
  };
}

const mapCategory = (category) => ({
  id: category.category,
  name: category.category,
});

const mapTicker = (ticker) => ({
  id: ticker.ticker,
  name: ticker.ticker,
});

const mapField = (field) => ({
  id: field,
  name: field,
});

const AllData = () => {
  const { classes } = useStyles();
  const {
    filtersLoading,
    bloombergFilters,

    categories,
    selectedCategory,
    setSelectedCategory,

    tickers,
    selectedTicker,
    setSelectedTicker,

    fields,
    selectedField,
    setSelectedField,

    selectedPresetDateRange,
    setSelectedPresetDateRange,

    selectedStartDate,
    setSelectedStartDate,

    selectedEndDate,
    setSelectedEndDate,

    bloombergData,
    bloombergDataLoading,
    bloombergDataError,
  } = useBloombergContext();

  const { minStartDate, maxEndDate } = bloombergFilters || {};
  const mappedCategories = [allOption, ...(categories || []).map(mapCategory)];
  const mappedTickers = [allOption, ...(tickers || []).map(mapTicker)];
  const mappedFields = [allOption, ...(fields || []).map(mapField)];

  const datas = bloombergData?.map(
    ({
      ticker,
      field,
      reportDate,
      numericValue,
      stringValue,
      dateValue,
      lastUpdated,
    }) =>
      createData(
        ticker,
        field,
        reportDate?.slice(0, 10),
        numericValue ?? "N/A",
        stringValue ?? "N/A",
        dateValue,
        lastUpdated,
      ),
  );

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedTicker(null);
  };

  const handleTickerChange = (ticker) => {
    setSelectedTicker(ticker);
  };

  const handleFieldChange = (field) => {
    setSelectedField(field);
  };

  return (
    <div className={classes.page}>
      <Grid container item spacing={2}>
        <Card
          style={{
            padding: "16px",
            width: "100%",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel id={"category"}>Category</InputLabel>
              <Autocomplete
                options={mappedCategories}
                getOptionLabel={(option) => option?.name || ""}
                value={
                  mappedCategories.find(
                    (item) => item.id === selectedCategory,
                  ) || null
                }
                onChange={(_, newValue) => {
                  handleCategoryChange(newValue ? newValue.id : null);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={filtersLoading}
                ListboxProps={{
                  style: {
                    width: "max-content",
                    minWidth: "100%",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label=""
                    placeholder="Select Category"
                    size="small"
                  />
                )}
                sx={{ width: 250 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel id={"ticker"}>Ticker</InputLabel>
              <Autocomplete
                options={mappedTickers}
                getOptionLabel={(option) => option?.name || ""}
                value={
                  mappedTickers.find((item) => item.id === selectedTicker) ||
                  null
                }
                onChange={(_, newValue) => {
                  handleTickerChange(newValue ? newValue.id : null);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={filtersLoading}
                ListboxProps={{
                  style: {
                    width: "max-content",
                    minWidth: "100%",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label=""
                    placeholder="Select Ticker"
                    size="small"
                  />
                )}
                sx={{ width: 250 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel id={"field"}>Field</InputLabel>
              <Autocomplete
                options={mappedFields}
                getOptionLabel={(option) => option?.name || ""}
                value={
                  mappedFields.find((item) => item.id === selectedField) || null
                }
                onChange={(_, newValue) => {
                  handleFieldChange(newValue ? newValue.id : null);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={filtersLoading}
                ListboxProps={{
                  style: {
                    width: "max-content",
                    minWidth: "100%",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label=""
                    placeholder="Select Field"
                    size="small"
                  />
                )}
                sx={{ width: 250 }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <InputLabel id={"report-date"}>Report Date Range</InputLabel>
              <DateRangeDatePickerWithPresets
                text="Report Date Range"
                minStartDate={minStartDate}
                maxEndDate={maxEndDate}
                setSelectedPresetDateRange={setSelectedPresetDateRange}
                setSelectedStartDate={setSelectedStartDate}
                setSelectedEndDate={setSelectedEndDate}
                selectedStartDate={selectedStartDate}
                selectedEndDate={selectedEndDate}
                isLoading={filtersLoading}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4} style={{ marginTop: "22px" }}>
              <AllDataExport
                columns={columns}
                datas={datas}
                disabled={
                  bloombergDataLoading ||
                  filtersLoading ||
                  !selectedCategory ||
                  !selectedTicker ||
                  !selectedField ||
                  !datas ||
                  !datas.length
                }
              />
            </Grid>
          </Grid>
        </Card>
        <Grid item xs={12} md={12} lg={12}>
          {selectedCategory &&
            selectedTicker &&
            selectedField &&
            selectedPresetDateRange && (
              <Typography
                variant="subtitle1"
                component="h2"
                sx={{
                  margin: "10px",
                  color: "#6a6a6a",
                }}
              >
                Showing All Market Data report for {selectedTicker} ticker under{" "}
                {selectedCategory} category having {selectedField} field for{" "}
                {selectedPresetDateRange === CUSTOM
                  ? `dates between ${selectedStartDate?.format(
                      "YYYY-MM-DD",
                    )} and ${selectedEndDate?.format("YYYY-MM-DD")}`
                  : selectedPresetDateRange}
                .
              </Typography>
            )}
        </Grid>

        <Grid size={{ xs: 12, md: 12 }}>
          {!selectedCategory || !selectedTicker || !selectedField ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                Please select the filters to see the appropriate data.
              </Typography>
            </Item>
          ) : !bloombergDataLoading && bloombergDataError ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                There was an error in fetching the data. Please try again later.
              </Typography>
            </Item>
          ) : (!bloombergDataLoading && !bloombergData) ||
            (!bloombergDataLoading && !bloombergData?.length) ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                No data available for the selected filters.
              </Typography>
            </Item>
          ) : (
            <Item>
              <ColumnGroupingTable
                columns={columns}
                datas={datas}
                loading={bloombergDataLoading}
              />
            </Item>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 12 }} sx={{ display: "none" }}>
          <Item>
            <WhatWeDo />
          </Item>
        </Grid>
      </Grid>
    </div>
  );
};

export default AllData;
